import {
  Signal,
  computed,
  signal,
  type ReadonlySignal,
  batch,
  effect,
} from "@preact/signals";
import {
  COLLECTION_2_CARDS,
  COLLECTION_3_CARDS,
  COLLECTION_ACCESSORIES,
  COLLECTION_FAMILY,
  COLLECTION_RINGS,
} from "src/data/tangem-wallets";
import { warehouseMap } from "src/data/warehouses";
import type { Locale, MedusaProduct } from "src/types";
import {
  couponManager,
  isCouponApplicableForProduct,
  initDiscounts,
} from "./discount";
import { newRingSku, ringSku } from "src/utils/constants";
import { getCurrencyFormatter, getPrice } from "../helpers";
import { accessory } from "./accessory";
import { freeShippingAmount } from "./freeShipping";
import { deepClone } from "@lib/util";

export type SetsType = "2" | "3" | "ring" | "family";
const persistStorageProductsKey = "selectedProducts";
export const productsInitialized = signal(false);

export const defaultPricePack3 = 6990;
export const defaultRingPrice = 16000;
export const defaultPrices = {
  2: 5490,
  3: defaultPricePack3,
  ring: defaultRingPrice,
  family: defaultPricePack3 + defaultPricePack3,
};

const defaultWarehouseStatus = {
  shipping_starts_after: null,
  is_available: true,
};

export const activeProduct = signal<MedusaProduct | null>(null);
export const countryInputText = signal("");
export const selectedPack = signal<SetsType | null>(null);
export let currencyFormater: Intl.NumberFormat;
export const activeCountry = signal<Record<string, string> | null>(null);
export const regionId = signal<string | null>(null);

type LastSelectedProducts = Record<SetsType, string | null>;

const lastSelectedProductsInCategory = signal<LastSelectedProducts>({
  2: null,
  3: null,
  ring: null,
  family: null,
});

export const getLastSelectedProductInSet = (set: SetsType) =>
  findProductDataBySku(
    sets.value?.[set],
    lastSelectedProductsInCategory.value[set] ?? "",
  ) ?? sets.value?.[set][0];

export const getFormattedPrice = (signal: Signal<number | null>) => {
  if (!signal.value) return "";
  return currencyFormater.format(signal.value);
};

const tryApplyDiscount = (
  price: number | null,
  product: MedusaProduct | null,
): number => {
  if (!product || !price) return 0;
  const applicableCoupons = couponManager.coupons.value.filter((coupon) =>
    isCouponApplicableForProduct(coupon, product),
  );
  if (applicableCoupons.length === 0) return price;

  const discountsSum = applicableCoupons.reduce(
    (acc, curr) => (acc += curr.amount),
    0,
  );
  const discountValue = (price * (100 - discountsSum)) / 100;
  return Math.round((discountValue + Number.EPSILON) * 100) / 100;
};

type Prices = Record<
  SetsType,
  {
    oldPrice: Signal<number | null>;
    price: ReadonlySignal<number>;
  }
>;

export const CLASSIC_SKU = "TG128X3-B";
export const getClassicCard = () =>
  findProductDataBySku(sets.peek()?.[3], CLASSIC_SKU);

const calculatePriceForSet = (set: SetsType) => {
  return tryApplyDiscount(
    prices[set].oldPrice.value,
    getLastSelectedProductInSet(set),
  );
};

export const prices: Prices = {
  2: {
    oldPrice: signal<number | null>(null),
    price: computed(() => calculatePriceForSet("2")),
  },
  3: {
    oldPrice: signal<number | null>(null),
    price: computed(() => calculatePriceForSet("3")),
  },
  ring: {
    oldPrice: signal<number | null>(null),
    price: computed(() => calculatePriceForSet("ring")),
  },
  family: {
    oldPrice: signal<number | null>(null),
    price: computed(() => calculatePriceForSet("family")),
  },
};

export const sets = signal<{
  2: MedusaProduct[];
  3: MedusaProduct[];
  ring: MedusaProduct[];
  family: MedusaProduct[];
}>({ "2": [], "3": [], ring: [], family: [] });

export const cardsBySet = computed(
  () => (selectedPack.value && sets.value[selectedPack.value]) ?? [],
);

export const isRingPackSelected = computed(() => selectedPack.value === "ring");
export const isFamilyPackSelected = computed(
  () => selectedPack.value === "family",
);

const selectProductInSet = (set: SetsType) => {
  const sku = lastSelectedProductsInCategory.value[set];
  activeProduct.value =
    sku === null ? null : findProductDataBySku(cardsBySet.value, sku);
};

export const activeOrFirstProductInSet = computed(() => {
  if (isRingPackSelected.value) {
    return getLastSelectedProductInSet("ring");
  }
  return activeProduct.value;
});

// change set and select first product if it's a card
export const changeSet = (set: SetsType) =>
  batch(() => {
    selectedPack.value = set;
    selectProductInSet(set);
  });

export const updatePrice = () => {
  if (!selectedPack.value || !activeProduct.value) return;
  const priceOriginal = getPrice(
    activeProduct.value,
    defaultPrices[selectedPack.value],
  );
  prices[selectedPack.value].oldPrice.value = priceOriginal;
};

export const currentPrice = computed(
  () => selectedPack.value && prices[selectedPack.value]?.price.value,
);

export const activeSku = computed(() => activeProduct?.value?.variants[0].sku);

export const isStealth = computed(() => activeSku.value === "TG-STEALTH");

export const USA_SKU = "TG-USA";
export const isUSA = computed(() => activeSku.value === USA_SKU);

export const hasUsaDuty = computed(
  () => activeProduct.value?.metadata?.["usa-high-duties"] === "true",
);

export const isLimited = computed(() =>
  Boolean(activeProduct.value?.metadata.limited),
);

export const limitedFeaturesKeys = computed(
  () => activeProduct.value?.metadata?.features || [],
);

export const activeCountryCode = computed(() => activeCountry.value?.code);

export const isSoldOut = computed(() => {
  if (!activeCountryCode.value || !activeProduct.value) return false;
  const activeCountry = activeCountryCode.value;
  const warehouse =
    hasUsaDuty.value && activeCountry === "us"
      ? "slo"
      : warehouseMap[activeCountry];
  const { is_available, shipping_starts_after } =
    activeProduct.value.inventory_details[warehouse];
  return (!is_available && shipping_starts_after === null) || false;
});

export const warehouseStatus = computed(() => {
  if (!activeCountryCode.value) return defaultWarehouseStatus;
  const activeCountry = activeCountryCode.value;
  const warehouse =
    hasUsaDuty.value && activeCountry === "us"
      ? "slo"
      : warehouseMap[activeCountry];
  return (
    activeProduct?.value?.inventory_details[warehouse] || defaultWarehouseStatus
  );
});

const getValidatedCategory = (
  value: string | null,
  defaultCategory: SetsType,
): SetsType => {
  if (value !== null && Object.keys(sets.peek()).includes(value)) {
    return value as SetsType;
  }
  return defaultCategory;
};

const getValidatedProductBySku = (
  value: string | null,
): MedusaProduct | null => {
  // we want users to select ring size consciously
  if (isRingPackSelected.peek() || !value) return null;

  return (
    cardsBySet.peek().find((product) => product.variants[0].sku === value) ??
    null
  );
};

type ProductSourceGetter = () => LastSelectedProducts | null;

const updatelastSelectedProductsInPersistStorage = (products: {
  [key: string]: string | null;
}) => {
  sessionStorage.setItem(persistStorageProductsKey, JSON.stringify(products));
};

const getLastSelectedProductsFromPersistStorage: ProductSourceGetter = () => {
  const products = sessionStorage.getItem(persistStorageProductsKey);
  try {
    if (products) {
      return JSON.parse(products);
    }
    return null;
  } catch {
    return null;
  }
};

// we want users to select ring size consciously
const getDefaultProduct = () =>
  isRingPackSelected.peek() ? null : cardsBySet.peek()[0];

export const getPriceNumber = (price: string) => Number(price.slice(1));

export const initProductsModel = async (
  locale: Locale,
  fetchProducts: () => Promise<{
    products: MedusaProduct[];
    freeShippingThresholdCost: number;
  }>,
  buildedCardIds: string[],
  defaultCategory: SetsType = "3",
) => {
  currencyFormater = getCurrencyFormatter(locale);

  const url = new URL(window.location.href);
  // -- reset values
  activeProduct.value = null;
  prices[2].oldPrice.value = null;
  prices[3].oldPrice.value = null;
  prices["ring"].oldPrice.value = null;

  const { products: allProducts, freeShippingThresholdCost } =
    await fetchProducts();

  freeShippingAmount.value = freeShippingThresholdCost;

  const allMainProducts = allProducts.filter(
    (product) =>
      buildedCardIds.includes(product.id) &&
      product.collection.title !== COLLECTION_ACCESSORIES,
  );

  const accessories = allProducts.filter(
    (product) => product.collection.title === COLLECTION_ACCESSORIES,
  );
  accessory.value = accessories[0];

  const productsMap = {
    [COLLECTION_2_CARDS]: "2",
    [COLLECTION_3_CARDS]: "3",
    [COLLECTION_RINGS]: "ring",
    [COLLECTION_FAMILY]: "family",
  } as const;

  sets.value = allMainProducts.reduce(
    (acc, product) => {
      const setType =
        productsMap[product.collection.title as keyof typeof productsMap];
      acc[setType].push(product);
      return acc;
    },
    {
      2: new Array<MedusaProduct>(),
      3: new Array<MedusaProduct>(),
      ring: new Array<MedusaProduct>(),
      family: new Array<MedusaProduct>(),
    },
  );
  sets.value.family = sets.value[3]
    .filter((product) =>
      product.tags.map((tag) => tag.value).includes("family"),
    )
    .map((product) => {
      const productCopy = deepClone(product);
      const classicCard = getClassicCard();
      const classicCardPrice = getPrice(classicCard, defaultPricePack3);
      productCopy.variants[0].prices.forEach((p) => {
        if (p.currency_code === "usd") {
          p.amount += classicCardPrice * 100; // convert to cents back
        }
      });
      if (productCopy.metadata && productCopy.metadata.priceBeforeDiscount) {
        const [sign, ...priceBeforeDiscount] =
          productCopy.metadata.priceBeforeDiscount;
        const priceBeforeDiscountAmount = Number(priceBeforeDiscount.join(""));
        const classicCardPriceBeforeDiscountAmount = classicCard?.metadata
          ?.priceBeforeDiscount
          ? getPriceNumber(classicCard.metadata.priceBeforeDiscount)
          : classicCardPrice;
        productCopy.metadata.priceBeforeDiscount = `${sign}${(
          priceBeforeDiscountAmount + classicCardPriceBeforeDiscountAmount
        ).toFixed(2)}`;
      }
      return productCopy;
    }); // init family pack

  selectedPack.value = getValidatedCategory(
    url.searchParams.get("cat"),
    defaultCategory,
  );

  lastSelectedProductsInCategory.value = Object.assign(
    {},
    {
      2: sets.value[2][0].variants[0].sku,
      3: sets.value[3][0].variants[0].sku,
      ring: null,
      family: sets.value.family[0]?.variants[0].sku,
    },
    getLastSelectedProductsFromPersistStorage() ?? {},
  );

  activeProduct.value =
    getValidatedProductBySku(url.searchParams.get("sku")) ??
    findProductDataBySku(
      cardsBySet.value,
      lastSelectedProductsInCategory.value[selectedPack.value] ?? "",
    ) ??
    getDefaultProduct();

  effect(() => {
    if (selectedPack.value) {
      lastSelectedProductsInCategory.value = {
        ...lastSelectedProductsInCategory.peek(),
        [selectedPack.value]: activeProduct.value?.variants[0].sku ?? null,
      };
    }
  });

  // update persistStorage with store values
  effect(() =>
    updatelastSelectedProductsInPersistStorage(
      lastSelectedProductsInCategory.value,
    ),
  );

  ([2, 3, "ring", "family"] as const).forEach((pack) => {
    prices[pack].oldPrice.value = getPrice(
      findProductDataBySku(
        sets.value[pack],
        lastSelectedProductsInCategory.value[pack] ??
          sets.value[pack][0]?.variants[0].sku,
      ),
      defaultPrices[pack],
    );
  });
  initDiscounts();
  productsInitialized.value = true;

  return allMainProducts;
};

// rings sku replacement depending on old ask availability

const urlReplacementMap = {
  [ringSku.RING6]: newRingSku.NEW_RING6,
  [ringSku.RING7]: newRingSku.NEW_RING7,
  [ringSku.RING8]: newRingSku.NEW_RING8,
  [ringSku.RING9]: newRingSku.NEW_RING9,
  [ringSku.RING10]: newRingSku.NEW_RING10,
  [ringSku.RING11]: newRingSku.NEW_RING11,
  [ringSku.RING12]: newRingSku.NEW_RING12,
  [ringSku.RING13]: newRingSku.NEW_RING13,
};

export const findProductDataBySku = (
  products: MedusaProduct[],
  sku: string,
): MedusaProduct | null =>
  products.find((product) => product.variants[0]?.sku === sku) ?? null;

export const getItemsWithUpdatedSkusWithoutGeo = ({ items }) =>
  items.map((ringItem) => ({
    ...ringItem,
    id: urlReplacementMap[ringItem.id],
  }));

type Item = {
  label: string;
  id: string;
  isSoldOut: boolean;
};

export const getItemsWithUpdatedSkus = ({
  ringsSet,
  countryCode,
  items,
}: {
  ringsSet: MedusaProduct[];
  countryCode: string;
  items: Item[];
}) => {
  return items.map((ringItem) => {
    const productData = findProductDataBySku(ringsSet, ringItem.id);
    const warehouse = warehouseMap[countryCode];
    if (productData?.inventory_details[warehouse].is_available === false) {
      return { ...ringItem, id: urlReplacementMap[ringItem.id] };
    } else return ringItem;
  });
};
